import { listingPhotoUrls } from '@app/utils/listing-photo-url.util';
import {
  formatUsCityStateZip,
  formatUsStreetLine,
  toTitleCaseWords,
} from '@app/utils/address-display.util';

export const PROPERTY_CARD_MAX_DOTS = 4;

export function formatPropertyPrice(price: unknown): string {
  const value = Number(price);
  if (!Number.isFinite(value)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPropertyNumber(value: unknown): string {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return String(value ?? '');
  }

  return new Intl.NumberFormat('en-US').format(number);
}

export function propertyTypeFrom(
  property: Record<string, unknown>,
  propertyTypeInput?: string,
): string {
  return String(propertyTypeInput || property['Type'] || '').toLowerCase();
}

export function formatPropertyStreet(property: Record<string, unknown>): string {
  const address = property['Address'] as Record<string, unknown> | undefined;
  if (!address) {
    return '';
  }

  return formatUsStreetLine({
    number: address['Number'],
    street: address['Street'],
    unit: address['Unit'],
  });
}

export function formatPropertySubdivision(property: Record<string, unknown>): string {
  const address = property['Address'] as Record<string, unknown> | undefined;
  return toTitleCaseWords(address?.['Subdivision']);
}

export interface PropertyCardStatSegment {
  value: string;
  label: string;
}

export function formatPropertyStatsLine(
  property: Record<string, unknown>,
  propertyType: string,
): string {
  return buildPropertyStatsSegments(property, propertyType)
    .map((segment) => `${segment.value} ${segment.label}`.trim())
    .join(' | ');
}

export function buildPropertyStatsSegments(
  property: Record<string, unknown>,
  propertyType: string,
): PropertyCardStatSegment[] {
  if (propertyType === 'residential') {
    return [
      { value: String(property['Bedrooms'] ?? '-'), label: 'bds' },
      { value: String(property['FullBaths'] ?? '-'), label: 'ba' },
      { value: formatPropertyNumber(property['Area']), label: 'sqft' },
    ];
  }

  if (propertyType === 'land') {
    return [{ value: String(property['Area'] ?? ''), label: 'acre lot' }];
  }

  if (propertyType === 'commercial' || propertyType === 'industrial') {
    return [{ value: formatPropertyNumber(property['Area']), label: 'sqft' }];
  }

  return [];
}

export function formatPropertyLocationLine(property: Record<string, unknown>): string {
  const address = property['Address'] as Record<string, unknown> | undefined;
  if (!address) {
    return formatPropertyStreet(property);
  }

  const street = formatPropertyStreet(property);
  const subdivision = formatPropertySubdivision(property);
  const cityStateZip = formatUsCityStateZip({
    city: address['City'],
    state: address['State'],
    zip: address['Zip'] ?? address['ZIP'],
  });
  const primary = street || subdivision;

  if (primary && cityStateZip) {
    return `${primary}, ${cityStateZip}`;
  }

  return primary || cityStateZip;
}

export function formatPropertyFullAddress(property: Record<string, unknown>): string {
  return formatPropertyLocationLine(property);
}

function appendStatsSegments(container: HTMLElement, segments: PropertyCardStatSegment[]): void {
  segments.forEach((segment, index) => {
    if (index > 0) {
      const divider = document.createElement('span');
      divider.className = 'property-card-stat-divider';
      divider.setAttribute('aria-hidden', 'true');
      divider.textContent = '|';
      container.appendChild(divider);
    }

    const group = document.createElement('span');
    group.className = 'property-card-stat-group';

    const value = document.createElement('span');
    value.className = 'property-card-stat-value';
    value.textContent = segment.value;

    const label = document.createElement('span');
    label.className = 'property-card-stat-label';
    label.textContent = ` ${segment.label}`;

    group.append(value, label);
    container.appendChild(group);
  });
}

export function appendPropertyCardDetails(
  container: HTMLElement,
  property: Record<string, unknown>,
  propertyType: string,
): void {
  const details = document.createElement('div');
  details.className = 'property-card-details';

  const price = document.createElement('div');
  price.className = 'property-card-price';
  price.textContent = formatPropertyPrice(property['Price']);
  details.appendChild(price);

  const segments = buildPropertyStatsSegments(property, propertyType);
  if (segments.length > 0) {
    const statsEl = document.createElement('div');
    statsEl.className = 'property-card-stats';
    appendStatsSegments(statsEl, segments);
    details.appendChild(statsEl);
  }

  const address = document.createElement('div');
  address.className = 'property-card-address';
  address.textContent = formatPropertyLocationLine(property);
  details.appendChild(address);

  container.appendChild(details);
}

/** Detail gallery mosaic URLs (medium WebP). Falls back to full URLs during migration. */
export function propertyGalleryImagePaths(property: Record<string, unknown>): string[] {
  return listingPhotoUrls(property, 'medium');
}

/** Full-size gallery URLs (detail pages, SEO). Falls back to card thumbs when needed. */
export function propertyImagePaths(property: Record<string, unknown>): string[] {
  return listingPhotoUrls(property, 'full');
}

/** Card/map image URLs (`fields=card`). Falls back to full URLs during migration. */
export function propertyCardImagePaths(property: Record<string, unknown>): string[] {
  return listingPhotoUrls(property, 'thumb');
}

export function favoriteIconLabel(property: Record<string, unknown>): string {
  return property['favorite'] ? 'favorite' : 'favorite_outline';
}

export const PROPERTY_CARD_FAVORITE_FILLED_PATH =
  'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

export const PROPERTY_CARD_FAVORITE_OUTLINE_PATH =
  'M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z';

export function propertyCardFavoriteSvgPath(isFavorite: boolean): string {
  return isFavorite ? PROPERTY_CARD_FAVORITE_FILLED_PATH : PROPERTY_CARD_FAVORITE_OUTLINE_PATH;
}
